import { useEffect, useState } from "react";

import { api } from "../lib/api";
import { dollars } from "../lib/format";
import type { IntradayScan } from "../lib/types";

/**
 * Live activity feed for the intraday strategy.
 *
 * Shows what the bot's scanner is seeing on each fire — one row per
 * scan (15-min cadence during RTH), expandable to per-symbol detail.
 * This is the "watch what the bot is doing" view.
 *
 * Pulls from `/v1/intraday-scans` (most recent 24). Polls every 30s.
 */

type Props = {
  // Optional limit; default 24 = last 6 hours at 15-min cadence
  limit?: number;
};

function formatRelative(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const deltaMin = Math.round((Date.now() - t) / 60_000);
  if (deltaMin < 1) return "just now";
  if (deltaMin < 60) return `${deltaMin} min ago`;
  if (deltaMin < 60 * 24) return `${Math.round(deltaMin / 60)}h ago`;
  return `${Math.round(deltaMin / (60 * 24))}d ago`;
}

function ScanRow({ scan }: { scan: IntradayScan }) {
  const [open, setOpen] = useState(false);
  const pick = scan.symbols.find((s) => s.is_pick) ?? null;
  return (
    <li className="border-b border-ink-700/50 last:border-b-0">
      <button
        className="flex w-full items-baseline justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-ink-800/40"
        onClick={() => setOpen((v) => !v)}
      >
        <div className="flex items-baseline gap-3">
          <span className="font-mono text-xs text-slate-500">
            {scan.scan_at_utc.slice(11, 16)}Z
          </span>
          {pick ? (
            <>
              <span className="rounded bg-emerald-900/40 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-emerald-300">
                FIRED
              </span>
              <span className="font-mono font-medium text-slate-100">
                {pick.symbol}
              </span>
              <span className="font-mono text-[10px] uppercase tracking-wider text-sky-300">
                {pick.setup_name}
              </span>
              {pick.setup_entry_cents != null && (
                <span className="text-xs text-slate-400">
                  @ {dollars(pick.setup_entry_cents)}
                </span>
              )}
            </>
          ) : (
            <>
              <span className="rounded bg-slate-800 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-slate-500">
                NO SETUP
              </span>
              <span className="text-xs text-slate-500">
                {scan.symbols.length} symbols scanned
              </span>
            </>
          )}
        </div>
        <span className="font-mono text-[10px] text-slate-600">
          {formatRelative(scan.scan_at_utc)}
        </span>
      </button>
      {open && (
        <div className="border-t border-ink-700/30 bg-ink-800/30 px-3 py-2">
          {scan.notes.length > 0 && (
            <div className="mb-2 text-xs text-amber-300">
              <span className="font-medium">notes:</span>{" "}
              {scan.notes.join(" • ")}
            </div>
          )}
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-1 py-1 text-left">symbol</th>
                <th className="px-1 py-1 text-right">close</th>
                <th className="px-1 py-1 text-right">vol</th>
                <th className="px-1 py-1 text-right">vol vs 20d</th>
                <th className="px-1 py-1 text-left">setup</th>
              </tr>
            </thead>
            <tbody>
              {scan.symbols.map((s) => {
                const volRatio =
                  s.avg_volume_20 && s.avg_volume_20 > 0 && s.last_volume != null
                    ? s.last_volume / s.avg_volume_20
                    : null;
                return (
                  <tr
                    key={s.symbol}
                    className={s.is_pick ? "text-emerald-300" : "text-slate-400"}
                  >
                    <td className="px-1 py-1 font-mono">
                      {s.symbol}
                      {s.is_pick && (
                        <span className="ml-1 text-[10px]">⭐</span>
                      )}
                    </td>
                    <td className="px-1 py-1 text-right font-mono">
                      {s.last_close_cents != null
                        ? dollars(s.last_close_cents)
                        : "—"}
                    </td>
                    <td className="px-1 py-1 text-right font-mono">
                      {s.last_volume != null
                        ? s.last_volume.toLocaleString()
                        : "—"}
                    </td>
                    <td className="px-1 py-1 text-right font-mono">
                      {volRatio != null
                        ? `${volRatio.toFixed(2)}×`
                        : "—"}
                    </td>
                    <td className="px-1 py-1">
                      {s.setup_name ? (
                        <>
                          <span className="rounded bg-sky-900/40 px-1.5 py-0.5 font-mono text-[10px] text-sky-300">
                            {s.setup_name}
                          </span>
                          {s.setup_evidence && (
                            <div className="mt-0.5 text-[10px] italic text-slate-500">
                              {s.setup_evidence.slice(0, 100)}
                            </div>
                          )}
                        </>
                      ) : s.error ? (
                        <span className="text-rose-400">{s.error.slice(0, 40)}</span>
                      ) : (
                        <span className="text-slate-600">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </li>
  );
}

export function IntradayActivityPanel({ limit = 24 }: Props) {
  const [scans, setScans] = useState<IntradayScan[]>([]);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.intradayScans(limit);
        if (!cancelled) {
          setScans(r.rows);
          setErr(null);
        }
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : "failed");
      }
    };
    tick();
    const id = window.setInterval(tick, 30_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [limit]);

  const fired = scans.filter((s) => s.pick_symbol != null).length;

  return (
    <section className="rounded-lg border border-ink-700 bg-ink-900">
      <div className="flex items-baseline justify-between border-b border-ink-700 px-3 py-2">
        <div className="text-sm">
          <span className="font-semibold text-slate-200">
            Live scanner activity
          </span>
          <span className="ml-2 text-xs font-normal text-slate-500">
            ({scans.length} scans, {fired} fired)
          </span>
        </div>
        <div className="text-[10px] uppercase tracking-wider text-slate-500">
          updates every 30s
        </div>
      </div>
      {err && (
        <div className="border-b border-ink-700 px-3 py-2 text-xs text-accent-err">
          {err}
        </div>
      )}
      {scans.length === 0 ? (
        <div className="px-3 py-6 text-center text-sm text-slate-500">
          No scans recorded yet.
          <div className="mt-1 text-xs">
            The bot's intraday scanner fires every 15 min during RTH on
            TSLA/COIN/BABA/META/NFLX/AMZN. Activity will appear here as
            scans complete.
          </div>
        </div>
      ) : (
        <ul>
          {scans.map((s) => (
            <ScanRow key={s.scan_at_utc} scan={s} />
          ))}
        </ul>
      )}
    </section>
  );
}
